import React from 'react'
import { ActiveCyclesWIthoutScreenProps } from './interfaces/ActiveCyclesWIthoutScreenProps';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import CycleSettings from '../../../../components/CycleSettings/CycleSettings';
import SearchScreen from '../../../../components/SearchScreen/SearchScreen';
import Modal from '../../../../components/Modal/Modal';
import ScreenCyclesTable from '../../../../components/ScreenCyclesTable/ScreenCyclesTable';
import CriticalDeviation from '../../../../components/CriticalDeviation';
import { CELSIUS_TEMPERATURE_DIFF_THRESHOLD } from '../../../../components/ScreenCyclesTable/utils/getTemperatureBorderClass';
import { useTranslation } from 'react-i18next';
import CycleStatus from '../../../../enums/cycle-status.enum';
import { useActiveCyclesWIthoutScreen } from './hooks/useActiveCyclesWIthoutScreen';
import DuplicateCycleModal from '../../../../components/DuplicateCycleModal/DuplicateCycleModal';
import CycleAttachment from '../../../../components/CycleAttachment/CycleAttachment';

const ActiveCyclesWIthoutScreen: React.FC<ActiveCyclesWIthoutScreenProps> = ({ cycles, isLoading, contractId }) => {
  const { t } = useTranslation();
  const {
    isLoading: isLoadingHook,

    cycleActions,

    getScreensByContractId,
    screensBySearch,

    //attach screen to cycle
    cycleIdAttachScreen,
    closeSelectedCycleAttachScreen,
    onAttachScreenToCycle,

    //cycle settings
    cycleIdSettings,
    cycleNameSettings,
    openSelectedCycleSettings,
    closeSelectedCycleSettings,

    //delete cycle from screen
    cycleIdDelete,
    closeSelectedCycleDelete,
    onAcceptSelectedCycleDelete,

    //cycle complete
    cycleIdComplete,
    closeSelectedCycleComplete,
    onAcceptSelectedCycleComplete,

    //cycle make inactive
    cycleIdMakeInactive,
    closeSelectedCycleMakeInactive,
    onAcceptSelectedCycleMakeInactive,

    //attach to cycle
    cycleIdAttach,
    closeSelectedCycleAttach,
    onAcceptSelectedCycleAttach,
    onSelectFile,
    isSaveAttachment,

    //multi-duplicate cycle
    cycleIdDuplicate,
    cycleNameDuplicate,
    closeSelectedCycleDuplicate,
    onCycleDuplicated,
  } = useActiveCyclesWIthoutScreen(contractId);

  if (isLoading || isLoadingHook) {
    return (
      <div className="flex flex-row items-center justify-center">
        <SpinnerLoader />
      </div>
    );
  }

  if (!cycles.length && (!isLoading || !isLoadingHook)) {
    return null;
  }

  return (
    <div className="c-active-cycles-without-screen relative flex flex-col overflow-hidden rounded-lg border border-foggy-mist bg-white p-2 shadow-15 dark:border-strokedark dark:bg-boxdark">
      <div className="min-xsm:flex-row flex w-full items-center justify-between gap-x-2 pb-2 max-xsm:flex-col">
        <span className="text-base font-bold max-md:pb-1 md:text-xl">
          {t('labels.cycleWithoutScreen')}
        </span>
        <CriticalDeviation disabled value={CELSIUS_TEMPERATURE_DIFF_THRESHOLD} />
      </div>

      <ScreenCyclesTable
        openSettings={openSelectedCycleSettings}
        screenCycles={cycles}
        actionsButtons={cycleActions}
        status={CycleStatus.Active}
        dragContractId={contractId}
        dragScreenId={null}
      />

      <Modal
        modalTitle="Cycle deletion"
        isVisible={!!cycleIdDelete}
        content="Are you sure you want to delete this cycle?"
        acceptBtnText="Delete"
        onClose={closeSelectedCycleDelete}
        onAccept={onAcceptSelectedCycleDelete}
        showAcceptBtn
        showCancelBtn
      />
      <Modal
        modalTitle="Complete Cycle"
        isVisible={!!cycleIdComplete}
        content="Are you sure you want to complete this cycle?"
        acceptBtnText="Complete"
        onClose={closeSelectedCycleComplete}
        onAccept={onAcceptSelectedCycleComplete}
        showAcceptBtn
        showCancelBtn
      />
      <Modal
        modalTitle="Make Cycle Inactive"
        isVisible={!!cycleIdMakeInactive}
        content="Are you sure you want to move this cycle back to inactive?"
        acceptBtnText="Make Inactive"
        onClose={closeSelectedCycleMakeInactive}
        onAccept={onAcceptSelectedCycleMakeInactive}
        showAcceptBtn
        showCancelBtn
      />
      <Modal
        modalTitle="Cycle Attachments"
        isVisible={!!cycleIdAttach}
        onClose={closeSelectedCycleAttach}
        onAccept={onAcceptSelectedCycleAttach}
        acceptBtnText={`${isSaveAttachment ? 'Saving...' : 'Save'}`}
        className="min-h-[500px] w-[800px]"
      >
        <CycleAttachment onSelectFile={onSelectFile} />
      </Modal>

      {!!cycleIdSettings && (
        <CycleSettings isVisible={!!cycleIdSettings} onClose={closeSelectedCycleSettings} cycleId={cycleIdSettings} cycleName={cycleNameSettings} />
      )}

      {!!cycleIdAttachScreen && (
        <SearchScreen
          visibility={!!cycleIdAttachScreen}
          onClose={closeSelectedCycleAttachScreen}
          getScreenByContractId={(payload: { query?: SearchScreen }) =>
            getScreensByContractId({ contractId, ...payload })
          }
          screensBySearch={screensBySearch}
          attachScreen={onAttachScreenToCycle}
        />
      )}

      <DuplicateCycleModal
        cycleId={cycleIdDuplicate}
        cycleName={cycleNameDuplicate}
        isVisible={!!cycleIdDuplicate}
        onClose={closeSelectedCycleDuplicate}
        onDuplicated={onCycleDuplicated}
      />
    </div>
  );
};

export default ActiveCyclesWIthoutScreen;
